import React, { useState } from "react";
import { Mic2, Play, Clock, ArrowRight, Headphones, Search } from "lucide-react";
import PageHero from "@/components/PageHero";
import FloatingEditButton from "@/components/FloatingEditButton";
import ShareButtons from "@/components/ShareButtons";
import { AlbumCompacto } from "@/components/GaleriaFotos";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import Card, { CardBody } from "@/components/ui/Card";
import SmartImage from "@/components/ui/SmartImage";
import EmptyState from "@/components/ui/EmptyState";
import { CardGridSkeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";

const PLATAFORMAS = ["Spotify", "Apple Podcasts", "Google Podcasts", "iVoox"];

export default function Podcast() {
  const { data: episodes, loading } = useCachedResource("podcast", () => apiGet("/podcast"));
  const [searchTerm, setSearchTerm] = useState("");

  const episodios = Array.isArray(episodes) ? episodes : [];
  const filteredEpisodes = episodios.filter(
    ep =>
      ep.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ep.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="podcast-page">
      <SEO
        title="Podcast"
        description="Episodios sobre evangelización digital, testimonios misioneros y reflexiones para vivir la fe en el entorno digital."
      />
      <PageHero slug="podcast" />

      {/* Presentación y buscador */}
      <Section tone="light" space="tight">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary mb-3 flex items-center gap-2">
              <Headphones className="w-3.5 h-3.5" aria-hidden="true" /> Escúchanos
            </p>
            <h2 className="font-heading font-bold mb-4" style={{ fontSize: "var(--text-h2)" }}>
              Nuestro Podcast
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Explora nuestros episodios sobre evangelización digital, testimonios misioneros y
              reflexiones para vivir la fe en el entorno digital. Contenido para inspirar y formar a
              la Iglesia de hoy.
            </p>
          </Reveal>

          <div className="w-full md:w-80 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <label htmlFor="buscar-episodios" className="sr-only">Buscar episodios</label>
            <input
              id="buscar-episodios"
              type="search"
              placeholder="Buscar episodios..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-border bg-card text-sm focus-ring"
            />
          </div>
        </div>
      </Section>

      {/* Listado de episodios */}
      <Section tone="muted" space="tight" className="min-h-[400px]">
        {loading && !episodios.length ? (
          <CardGridSkeleton count={3} />
        ) : filteredEpisodes.length === 0 ? (
          <EmptyState
            icon={Mic2}
            title="No hay episodios que coincidan"
            description="Prueba con otros términos de búsqueda."
          />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEpisodes.map((ep, i) => (
              <Reveal key={ep._id || i} delay={Math.min(i, 5) * 70} distance={28}>
                {/* La tarjeta entera es el enlace al episodio. Antes había
                    además un enlace de reproducción invisible superpuesto, sin
                    texto ni aria-label: un destino tabulable sin nombre. */}
                <Card href={ep.url} className="flex flex-col h-full">
                  <div className="relative">
                    <SmartImage
                      src={ep.image}
                      alt=""
                      ratio="16 / 9"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/20">
                          <Mic2 className="w-16 h-16 text-primary/20" aria-hidden="true" />
                        </div>
                      }
                    />
                    <div className="absolute top-4 left-4 bg-primary text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg">
                      EPISODIO {ep.episode || i + 1}
                    </div>
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-base bg-black/40"
                    >
                      <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-2xl scale-90 group-hover:scale-100 transition-transform duration-base ease-out-apple">
                        <Play className="w-7 h-7 text-primary fill-primary ml-1" />
                      </div>
                    </div>
                  </div>

                  <CardBody className="flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-lg leading-tight mb-3 group-hover:text-primary transition-colors duration-fast line-clamp-2">
                      {ep.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-6 flex-1">
                      {ep.description || "Sin descripción disponible para este episodio."}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
                      {ep.duration ? (
                        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {ep.duration}
                        </div>
                      ) : (
                        <span />
                      )}
                      <span className="inline-flex items-center gap-2 text-xs font-bold text-primary group-hover:gap-3 transition-[gap] duration-fast">
                        ESCUCHAR AHORA <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </span>
                    </div>
                  </CardBody>
                </Card>

                {/* Compartir queda fuera de la tarjeta-enlace: anidar botones
                    dentro de un <a> es HTML inválido y rompe el teclado. */}
                <AlbumCompacto fotos={ep.gallery} className="mt-3" />
                <div className="mt-3 flex justify-end">
                  <ShareButtons
                    compact
                    url={ep.url || "/podcast"}
                    title={ep.title}
                    description={ep.description || ""}
                  />
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </Section>

      {/* Plataformas */}
      <Section tone="dark" width="reading" className="relative overflow-hidden" innerClassName="text-center relative z-10">
        <div className="absolute top-0 right-0 opacity-10 translate-x-1/4 -translate-y-1/4 pointer-events-none" aria-hidden="true">
          <Mic2 className="w-96 h-96" />
        </div>
        <Reveal>
          <h2 className="font-heading font-bold mb-6" style={{ fontSize: "var(--text-h2)" }}>
            Disponible en todas las plataformas
          </h2>
          <p className="text-white/80 mb-10 text-lg">
            Suscríbete a nuestro podcast en tu plataforma favorita y no te pierdas ningún episodio.
            Lleva la formación digital contigo donde vayas.
          </p>
          <ul className="flex flex-wrap justify-center gap-4 list-none">
            {PLATAFORMAS.map(platform => (
              <li
                key={platform}
                className="px-6 py-3 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm font-semibold text-sm"
              >
                {platform}
              </li>
            ))}
          </ul>
        </Reveal>
      </Section>

      <FloatingEditButton slug="podcast" />
    </div>
  );
}
